/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useSearchParams } from 'react-router-dom';
import { type FC, type KeyboardEvent, useState, useEffect } from 'react';

import { brandConfig } from 'brand-config';
import { Button, Form } from '@coze-arch/coze-design';
import { useSystemConfig } from '@adp-foundation/global-store';
import { I18n } from '@adp-arch/i18n';
import { SignFrame, SignPanel } from '@adp-arch/bot-semi';
// @ts-expect-error - brand config may not have types in all environments

import { useLoginService, useAuthMethodsService } from './service';
import { getOIDCLoginURL } from './oidc';
import { ForcePasswordChange } from './force-password-change';
import { Favicon } from './favicon';
import {
  getDefaultLoginMethod,
  isLoginMethodEnabled,
  type LoginMethod,
} from './auth-methods';

const redirectToOIDCLogin = (profileID: string, redirectURL: string | null) => {
  window.location.href = getOIDCLoginURL(profileID, redirectURL);
};

const OIDCLoginButtons: FC<{
  profiles: { id: string; name: string }[] | undefined;
  redirectURL: string | null;
}> = ({ profiles, redirectURL }) => (
  <>
    {profiles?.map((profile, index) => (
      <Button
        key={profile.id || index}
        className="mb-[12px] w-full"
        onClick={() => redirectToOIDCLogin(profile.id, redirectURL)}
        theme="solid"
        color="primary"
      >
        Login with {profile.name}
      </Button>
    ))}
  </>
);

const useBrandTitle = (productName?: string) => {
  useEffect(() => {
    const title =
      productName || brandConfig?.title?.replace(/\s+ADP$/i, '') || 'ADP';
    document.title = title;
    const timer = setTimeout(() => {
      document.title = title;
    }, 100);
    return () => clearTimeout(timer);
  }, [productName]);
};

// eslint-disable-next-line @coze-arch/max-line-per-function -- Keep authentication method state and conditional forms together in the page component.
export const LoginPage: FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [hasError, setHasError] = useState(false);
  const [selectedLoginMethod, setSelectedLoginMethod] =
    useState<LoginMethod | null>(null);
  const [searchParams] = useSearchParams();
  const { showRegisterButton, brandConfiguration } = useSystemConfig();

  const { data: authMethods, loading: authLoading } = useAuthMethodsService();
  const defaultLoginMethod = authMethods
    ? getDefaultLoginMethod(authMethods)
    : null;
  const loginMethod =
    selectedLoginMethod &&
    authMethods &&
    isLoginMethodEnabled(selectedLoginMethod, authMethods)
      ? selectedLoginMethod
      : defaultLoginMethod;
  const localAuthEnabled = authMethods?.enable_local_auth === true;
  const ldapEnabled = authMethods?.enable_ldap === true;
  const showMethodSwitch = localAuthEnabled && ldapEnabled;
  const hasPasswordLogin = localAuthEnabled || ldapEnabled;
  const isLDAPLogin = loginMethod === 'ldap';
  const registrationEnabled =
    authMethods?.enable_register ?? showRegisterButton;

  const {
    login,
    register,
    loginLoading,
    registerLoading,
    loginError,
    passwordChangeRequired,
    changePassword,
    passwordChangeLoading,
    passwordChangeError,
  } = useLoginService({
    identifier,
    password,
    loginMethod: loginMethod ?? 'local',
  });

  const submitDisabled = !identifier || !password || hasError;
  useBrandTitle(brandConfiguration?.product_name);

  const selectLoginMethod = (method: LoginMethod) => {
    setSelectedLoginMethod(method);
    setIdentifier('');
    setPassword('');
    setHasError(false);
  };

  const handleMethodSwitchKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    event.preventDefault();
    selectLoginMethod(isLDAPLogin ? 'local' : 'ldap');
  };

  return (
    <SignFrame brandNode={null}>
      <SignPanel className="w-[600px] min-h-[640px] md:!h-auto md:max-h-[calc(100vh-32px)] md:overflow-y-auto pb-[48px] pt-[96px]">
        <div className="flex flex-col items-center w-full h-full">
          <Favicon />
          <div className="text-[24px] font-medium coze-fg-plug leading-[36px] mt-[32px]">
            {brandConfiguration?.login_welcome_message ||
              `Welcome to ${brandConfiguration?.product_name || brandConfig?.displayName?.replace(/\s+ADP$/i, '') || 'ADP'}`}
          </div>
          <div className="mt-[64px] w-[320px] flex flex-col items-stretch [&_.semi-input-wrapper]:overflow-hidden">
            {passwordChangeRequired ? (
              <ForcePasswordChange
                changePassword={changePassword}
                loading={passwordChangeLoading}
                error={passwordChangeError}
              />
            ) : authLoading ? (
              <div className="text-center py-4">Loading...</div>
            ) : (
              <>
                {hasPasswordLogin && loginMethod ? (
                  <>
                    {showMethodSwitch ? (
                      <div className="mb-[16px]">
                        <div className="mb-[8px] flex items-center justify-between px-[2px]">
                          <span className="coze-fg-secondary text-[12px] font-medium tracking-[0.08em] uppercase">
                            Sign-in method
                          </span>
                          <span className="coze-fg-secondary text-[12px]">
                            Choose your account type
                          </span>
                        </div>
                        <div
                          aria-label="Sign-in method"
                          className="grid grid-cols-2 gap-[6px] rounded-[12px] border border-[var(--coz-stroke-primary)] bg-[var(--coz-bg-secondary)] p-[6px]"
                          role="tablist"
                        >
                          <Button
                            aria-controls="login-credentials"
                            aria-selected={!isLDAPLogin}
                            className={`h-auto min-h-[74px] !justify-start !rounded-[9px] !px-[12px] !py-[10px] text-left ${
                              isLDAPLogin
                                ? '!bg-transparent !text-[var(--coz-fg-primary)] hover:!bg-[#e8f1ff]'
                                : '!bg-[#2563eb] !text-white hover:!bg-[#1d4ed8] !shadow-[0_2px_8px_rgba(37,99,235,0.25)]'
                            }`}
                            data-testid="login.auth-method.local"
                            htmlType="button"
                            id="login-method-local"
                            onClick={() => selectLoginMethod('local')}
                            onKeyDown={handleMethodSwitchKeyDown}
                            role="tab"
                            tabIndex={isLDAPLogin ? -1 : 0}
                            theme={isLDAPLogin ? 'borderless' : 'solid'}
                            type="tertiary"
                          >
                            <span className="flex flex-col items-start gap-[3px]">
                              <span className="text-[14px] font-semibold leading-[18px]">
                                LOCAL
                              </span>
                              <span
                                className={`text-[12px] leading-[16px] ${
                                  isLDAPLogin
                                    ? 'coze-fg-secondary'
                                    : 'text-white/80'
                                }`}
                              >
                                Platform email account
                              </span>
                            </span>
                          </Button>
                          <Button
                            aria-controls="login-credentials"
                            aria-selected={isLDAPLogin}
                            className={`h-auto min-h-[74px] !justify-start !rounded-[9px] !px-[12px] !py-[10px] text-left ${
                              isLDAPLogin
                                ? '!bg-[#059669] !text-white hover:!bg-[#047857] !shadow-[0_2px_8px_rgba(5,150,105,0.25)]'
                                : '!bg-transparent !text-[var(--coz-fg-primary)] hover:!bg-[#ecfdf5]'
                            }`}
                            data-testid="login.auth-method.ldap"
                            htmlType="button"
                            id="login-method-ldap"
                            onClick={() => selectLoginMethod('ldap')}
                            onKeyDown={handleMethodSwitchKeyDown}
                            role="tab"
                            tabIndex={isLDAPLogin ? 0 : -1}
                            theme={isLDAPLogin ? 'solid' : 'borderless'}
                            type="tertiary"
                          >
                            <span className="flex flex-col items-start gap-[3px]">
                              <span className="text-[14px] font-semibold leading-[18px]">
                                LDAP
                              </span>
                              <span
                                className={`text-[12px] leading-[16px] ${
                                  isLDAPLogin
                                    ? 'text-white/80'
                                    : 'coze-fg-secondary'
                                }`}
                              >
                                Organization directory
                              </span>
                            </span>
                          </Button>
                        </div>
                      </div>
                    ) : ldapEnabled ? (
                      <div
                        className="mb-[16px] flex items-center gap-[10px] rounded-[10px] border border-[var(--coz-stroke-primary)] bg-[var(--coz-bg-secondary)] px-[14px] py-[12px]"
                        id="login-method-ldap"
                      >
                        <div>
                          <p className="text-[14px] font-semibold leading-[18px] coze-fg-primary">
                            LDAP
                          </p>
                          <p className="coze-fg-secondary mt-[2px] text-[12px] leading-[16px]">
                            Organization directory account
                          </p>
                        </div>
                      </div>
                    ) : null}
                    <div
                      aria-labelledby={`login-method-${loginMethod}`}
                      className="rounded-[10px] border border-[var(--coz-stroke-primary)] bg-[var(--coz-bg-primary)] p-[12px]"
                      id="login-credentials"
                      role="tabpanel"
                    >
                      {isLDAPLogin || registrationEnabled ? (
                        <p className="coze-fg-secondary mb-[10px] text-[12px] leading-[16px]">
                          {isLDAPLogin
                            ? 'Use the account issued by your organization.'
                            : 'Use the email address registered with this platform.'}
                        </p>
                      ) : null}
                      <Form
                        key={loginMethod}
                        onErrorChange={errors => {
                          setHasError(Object.keys(errors).length > 0);
                        }}
                      >
                        <Form.Input
                          data-testid={
                            isLDAPLogin
                              ? 'login.input.ldap-account'
                              : 'login.input.email'
                          }
                          field="identifier"
                          label={isLDAPLogin ? 'LDAP account' : 'Email'}
                          rules={[
                            {
                              required: true,
                              message: isLDAPLogin
                                ? 'Enter your LDAP account'
                                : I18n.t('open_source_login_placeholder_email'),
                            },
                          ]}
                          onChange={newVal => {
                            setIdentifier(newVal);
                          }}
                          placeholder={
                            isLDAPLogin
                              ? 'Username, email, or UPN'
                              : I18n.t('open_source_login_placeholder_email')
                          }
                          type={isLDAPLogin ? 'text' : 'email'}
                        />
                        <Form.Input
                          data-testid="login.input.password"
                          field="password"
                          label="Password"
                          rules={[
                            {
                              required: true,
                              message: I18n.t(
                                'open_source_login_placeholder_password',
                              ),
                            },
                          ]}
                          type="password"
                          onChange={setPassword}
                          placeholder={I18n.t(
                            'open_source_login_placeholder_password',
                          )}
                        />
                      </Form>
                    </div>
                    <Button
                      data-testid="login.button.login"
                      className="mt-[12px]"
                      disabled={submitDisabled || registerLoading}
                      onClick={login}
                      loading={loginLoading}
                      color="primary"
                    >
                      {I18n.t('login_button_text')}
                    </Button>
                    {loginError ? (
                      <div
                        role="alert"
                        className="mt-[12px] rounded-[6px] border border-red-300 bg-red-50 px-[12px] py-[10px] text-sm leading-5 text-red-700"
                      >
                        {loginError}
                      </div>
                    ) : null}
                    {!isLDAPLogin && registrationEnabled ? (
                      <Button
                        data-testid="login.button.signup"
                        className="mt-[20px]"
                        disabled={submitDisabled || loginLoading}
                        onClick={register}
                        loading={registerLoading}
                        color="primary"
                      >
                        {I18n.t('register')}
                      </Button>
                    ) : null}
                  </>
                ) : null}

                {authMethods?.oidc_methods?.length > 0 && hasPasswordLogin ? (
                  <div className="flex items-center my-4">
                    <div className="flex-1 h-px bg-gray-200"></div>
                    <span className="px-3 text-sm text-gray-500">OR</span>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>
                ) : null}

                {/* OIDC Buttons */}
                <OIDCLoginButtons
                  profiles={authMethods?.oidc_methods}
                  redirectURL={searchParams.get('redirect')}
                />
              </>
            )}
          </div>
        </div>
      </SignPanel>
    </SignFrame>
  );
};
